import { FadeVerticalScroll } from "@/components/vertical-scroll/FadeVerticalScroll";
import { MdsDropdownItem, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { MdsDropdownButtonItemComponent } from "@/design-system/components/dropdown/MdsDropdownButtonItemComponent";
import { MdsDropdownDetailItemComponent } from "@/design-system/components/dropdown/MdsDropdownDetailItemComponent";
import { MdsDropdownDividerItemComponent } from "@/design-system/components/dropdown/MdsDropdownDividerItemComponent";
import { MdsDropdownScrollableSection } from "@/design-system/components/dropdown/types";
import { css, cx } from "@/domains/emotion";
import { useTheme } from "@emotion/react";
import { FC } from "react";

interface MdsDropdownItemComponentProps {
  className?: string;
  tooltipId: string;
  item: MdsDropdownItem;
  onOpenChange?: (value: boolean) => void;
  onHover?: ({ itemId }: { itemId?: string }) => void;
}

const MdsDropdownItemComponent: FC<MdsDropdownItemComponentProps> = ({
  className,
  tooltipId,
  item,
  onOpenChange,
  onHover,
}) => {
  const theme = useTheme();
  const mdsDropdownItemContentDefaultStyles = css({
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    color: theme.colors.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

  switch (item.kind) {
    case MdsDropdownItemKind.Button: {
      return (
        <MdsDropdownButtonItemComponent
          className={className}
          item={item}
          onOpenChange={onOpenChange}
          tooltipId={tooltipId}
          onHover={onHover}
          isSelected={item.isSelected}
        />
      );
    }
    case MdsDropdownItemKind.Divider: {
      return <MdsDropdownDividerItemComponent className={className} item={item} />;
    }
    case MdsDropdownItemKind.Detail: {
      return <MdsDropdownDetailItemComponent className={className} item={item} />;
    }
    case MdsDropdownItemKind.Other: {
      return (
        <div className={cx(mdsDropdownItemContentDefaultStyles, className)}>{item.content}</div>
      );
    }
    case MdsDropdownItemKind.ScrollableSection: {
      return (
        <MdsDropdownScrollableSectionItemComponent
          item={item}
          tooltipId={tooltipId}
          onHover={onHover}
          onOpenChange={onOpenChange}
        />
      );
    }
  }
};

interface MdsDropdownScrollableSectionItemComponentProps {
  tooltipId: string;
  item: MdsDropdownScrollableSection;
  onOpenChange?: (value: boolean) => void;
  onHover?: ({ itemId }: { itemId?: string }) => void;
  className?: string;
}

const MdsDropdownScrollableSectionItemComponent: FC<
  MdsDropdownScrollableSectionItemComponentProps
> = ({ tooltipId, item, onOpenChange, onHover, className }) => {
  return (
    <FadeVerticalScroll>
      {item.items.map((item: MdsDropdownItem) =>
        mdsDropdownItemComponentMapper({
          item,
          onOpenChange,
          tooltipId,
          onHover,
          className: cx(fullWidthStyles, className),
        })
      )}
    </FadeVerticalScroll>
  );
};

export const mdsDropdownItemComponentMapper = (args: {
  item: MdsDropdownItem;
  onOpenChange?: (value: boolean) => void;
  tooltipId: string;
  onHover?: ({ itemId }: { itemId?: string }) => void;
  className?: string;
}) => <MdsDropdownItemComponent {...args} key={args.item.id} />;

const fullWidthStyles = css({
  width: "100%",
});
